import React, { useState, useEffect } from 'react';

interface LoadingOverlayProps {
  isVisible: boolean;
  messages?: string[];
  /** When set, shows this message instead of cycling through messages. Used for retry status. */
  overrideMessage?: string;
  altText?: string;
  fullScreen?: boolean; // When true, covers entire viewport with fixed positioning
  contained?: boolean; // When true, uses fixed positioning to cover entire viewport (full screen)
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isVisible,
  messages = [],
  overrideMessage,
  altText = 'Larry Loading',
  fullScreen = false,
  contained = true, // Default to true - always contained within parent
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentMessage, setCurrentMessage] = useState<string>('');

  // Handle message cycling with fade in/out
  useEffect(() => {
    if (!isVisible || messages.length === 0) {
      setCurrentMessage('');
      setCurrentMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setCurrentMessageIndex(prevIndex => {
        const nextIndex = prevIndex + 1;
        if (nextIndex >= messages.length) {
          return 0; // Loop back to start
        }
        return nextIndex;
      });
    }, 3000); // Change message every 3 seconds

    return () => clearInterval(interval);
  }, [isVisible, messages]);

  // Handle message display with fade effect
  useEffect(() => {
    if (!isVisible || messages.length === 0) {
      setCurrentMessage('');
      return;
    }

    // Fade out current message
    setCurrentMessage('');

    // Fade in new message after a short delay
    const timeout = setTimeout(() => {
      setCurrentMessage(messages[currentMessageIndex]);
    }, 300);

    return () => clearTimeout(timeout);
  }, [currentMessageIndex, messages, isVisible]);

  if (!isVisible) return null;

  // Force full screen overlay when contained or fullScreen is true
  // Both use fixed positioning to cover entire viewport
  const overlayStyle =
    contained || fullScreen
      ? {
          position: 'fixed' as const,
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
          zIndex: 9999,
        }
      : {
          position: 'absolute' as const,
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          zIndex: 50,
        };

  return (
    <div className='bg-watercolor-gradient' style={overlayStyle}>
      {/* Content container - centered within the overlay using flexbox */}
      <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center max-w-sm px-4'>
        {/* Floating watercolor blobs */}
        <div className='absolute -top-4 -right-6 sm:-top-6 sm:-right-8 w-10 h-10 sm:w-14 sm:h-14 bg-watercolor-blob rounded-full animate-float opacity-50'></div>
        <div className='absolute -bottom-4 -left-6 sm:-bottom-6 sm:-left-8 w-8 h-8 sm:w-10 sm:h-10 bg-watercolor-blob rounded-full animate-bounce-slow opacity-60'></div>
        <div
          className='absolute top-3 left-6 sm:top-4 sm:left-8 w-4 h-4 sm:w-6 sm:h-6 bg-watercolor-blob rounded-full animate-float opacity-40'
          style={{ animationDelay: '1.5s' }}
        ></div>

        {/* Larry mascot with medium bounce animation */}
        <div className='animate-bounce'>
          <img
            src='/larry-shock-fwd.svg'
            alt={altText}
            className='w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 drop-shadow-lg'
          />
        </div>

        {/* Cute loading dots */}
        <div className='flex justify-center items-center gap-0.5 mt-2'>
          <div
            className='w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 bg-primary-300 rounded-full animate-bounce'
            style={{ animationDelay: '0s' }}
          ></div>
          <div
            className='w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 bg-primary-300 rounded-full animate-bounce'
            style={{ animationDelay: '0.2s' }}
          ></div>
          <div
            className='w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 bg-primary-300 rounded-full animate-bounce'
            style={{ animationDelay: '0.4s' }}
          ></div>
        </div>

        {/* Messages */}
        <div className='text-center mt-4 sm:mt-6 min-h-[2rem] sm:min-h-[2.5rem] flex items-center justify-center'>
          {/* Override message (e.g. retry status) or cycling messages */}
          {(overrideMessage || (messages.length > 0 && currentMessage)) && (
            <div className='bg-primary-100 px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-sm animate-fade-in-out'>
              <p className='text-gray-800 font-medium text-xs sm:text-sm'>
                {overrideMessage ?? currentMessage}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoadingOverlay;
